import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Users, TrendingUp, Shield, ArrowRight, CheckCircle2, Star, MapPin, Sparkles, Truck, Wrench, Quote, TrendingDown, RefreshCw } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useState, useEffect, useContext } from "react";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Live ticker data - simulated recent activity
const tickerItems = [
  { text: "₹4.2Cr deal closed in Pune", time: "3 hrs ago", type: "deal" },
  { text: "Bakery in Nashik listed", time: "5 hrs ago", type: "listing" },
  { text: "₹85L restaurant acquired in Mumbai", time: "8 hrs ago", type: "deal" },
  { text: "Manufacturing unit in Ahmedabad listed", time: "12 hrs ago", type: "listing" },
  { text: "₹1.2Cr logistics business sold in Delhi", time: "1 day ago", type: "deal" },
  { text: "IT services company in Bangalore listed", time: "1 day ago", type: "listing" },
];

export default function Home() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [featuredListings, setFeaturedListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquiryDialog, setEnquiryDialog] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: ""
  });
  const [tickerIndex, setTickerIndex] = useState(0);

  useEffect(() => {
    fetchFeaturedListings();
    // Rotate ticker every 4 seconds
    const tickerInterval = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % tickerItems.length);
    }, 4000);
    return () => clearInterval(tickerInterval);
  }, []);

  const fetchFeaturedListings = async () => {
    try {
      const { data } = await api.get("/listings");
      // Get top 3 approved listings
      setFeaturedListings(data.slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch listings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Franchise opportunities data
  const franchises = [
    {
      id: 1,
      name: "Fast Food Franchise",
      brand: "QuickBite Express",
      investment: "₹25-30 Lakhs",
      area: "1000-1500 sq ft",
      roi: "25-30%",
      support: "Complete setup & training",
      locations: "Pan India",
      image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=500&q=80"
    },
    {
      id: 2,
      name: "Fitness Center Franchise",
      brand: "FitZone Gyms",
      investment: "₹40-50 Lakhs",
      area: "2000-3000 sq ft",
      roi: "30-35%",
      support: "Equipment & marketing",
      locations: "Tier 1 & 2 Cities",
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=500&q=80"
    },
    {
      id: 3,
      name: "Education Franchise",
      brand: "SmartKids Learning",
      investment: "₹15-20 Lakhs",
      area: "800-1200 sq ft",
      roi: "35-40%",
      support: "Curriculum & training",
      locations: "All Major Cities",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=500&q=80"
    },
    {
      id: 4,
      name: "Cafe Franchise",
      brand: "Coffee Culture",
      investment: "₹20-25 Lakhs",
      area: "600-1000 sq ft",
      roi: "28-32%",
      support: "Full setup & branding",
      locations: "Metro Cities",
      image: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=500&q=80"
    }
  ];

  const features = [
    { title: "Verified Listings", description: "All businesses are verified for authenticity", icon: Shield },
    { title: "Expert Consultants", description: "Connect with experienced business advisors", icon: Users },
    { title: "Secure Transactions", description: "Safe and secure payment processing", icon: CheckCircle2 },
    { title: "Growth Support", description: "Comprehensive guidance for scaling your business", icon: TrendingUp },
  ];

  const sponsoredBanner = {
    title: "Looking to Sell Your Business?",
    description: "Get FREE business valuation and connect with serious buyers instantly",
    cta: "Get Started Free",
    bgGradient: "from-slate-700 to-slate-800"
  };

  const handleFranchiseEnquiry = (franchise) => {
    if (!user) {
      toast.error("Please login to enquire about franchises");
      navigate("/auth");
      return;
    }
    setSelectedFranchise(franchise);
    setEnquiryForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      message: `I am interested in the ${franchise.brand} franchise. Please provide me with more details.`
    });
    setEnquiryDialog(true);
  };

  const [submittingEnquiry, setSubmittingEnquiry] = useState(false);

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    if (!selectedFranchise) return;
    
    setSubmittingEnquiry(true);
    try {
      await api.post("/franchise-enquiries", {
        franchise_id: String(selectedFranchise.id),
        franchise_name: selectedFranchise.name,
        franchise_brand: selectedFranchise.brand,
        name: enquiryForm.name,
        email: enquiryForm.email,
        mobile: enquiryForm.mobile,
        message: enquiryForm.message
      });
      toast.success(`Thank you! We'll contact you soon about ${selectedFranchise.brand} franchise.`);
      setEnquiryDialog(false);
      setEnquiryForm({ name: "", email: "", mobile: "", message: "" });
    } catch (error) {
      console.error("Failed to submit enquiry:", error);
      toast.error(error.response?.data?.detail || "Failed to submit enquiry. Please try again.");
    } finally {
      setSubmittingEnquiry(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 pt-24 md:pt-32 overflow-hidden bg-slate-900" data-testid="hero-section">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left: Headline & CTAs */}
            <div className="space-y-6 md:space-y-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
                India's #1 Platform to{' '}
                <span className="text-orange-500">Buy, Sell & Revive</span>{' '}
                Businesses
              </h1>
              <p className="text-lg md:text-xl text-slate-300 max-w-lg">
                Transform struggling businesses into success stories. Connect with verified buyers, sellers & expert consultants.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={user ? "/list-business" : "/auth"}>
                  <Button 
                    className="bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/30 h-14 px-8 text-lg font-semibold w-full sm:w-auto" 
                    data-testid="list-business-cta"
                  >
                    List My Business
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button 
                    variant="outline" 
                    className="border-2 border-slate-500 text-white hover:bg-slate-800 hover:border-slate-400 h-14 px-8 text-lg font-semibold w-full sm:w-auto bg-transparent" 
                    data-testid="browse-businesses-cta"
                  >
                    Browse Businesses
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right: Split-screen transformation visual */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                {/* Distressed Business */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent z-10 rounded-xl"></div>
                  <img
                    src="https://images.unsplash.com/photo-1606189934846-a527add8a77b?w=500&q=80"
                    alt="Distressed business"
                    className="rounded-xl w-full h-48 md:h-64 object-cover grayscale brightness-75"
                  />
                  <div className="absolute bottom-3 left-3 z-20 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-400" />
                    <span className="text-white text-sm font-medium">Struggling</span>
                  </div>
                </div>
                
                {/* Arrow in middle */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 hidden md:flex">
                  <div className="bg-orange-500 rounded-full p-2 shadow-lg shadow-orange-500/50">
                    <RefreshCw className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                {/* Thriving Business */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/60 to-transparent z-10 rounded-xl"></div>
                  <img
                    src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=500&q=80"
                    alt="Thriving business"
                    className="rounded-xl w-full h-48 md:h-64 object-cover"
                  />
                  <div className="absolute bottom-3 right-3 z-20 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                    <span className="text-white text-sm font-medium">Thriving</span>
                  </div>
                </div>
              </div>
              
              {/* Transformation badge */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 shadow-xl">
                <span className="text-xs md:text-sm text-slate-300">
                  <span className="text-orange-500 font-bold">500+</span> businesses transformed
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Live Activity Ticker */}
      <section className="bg-slate-800 border-y border-slate-700 overflow-hidden" data-testid="live-ticker">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center py-3 px-4">
            <div className="flex items-center gap-2 pr-4 border-r border-slate-600 mr-4 shrink-0">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Live</span>
            </div>
            <div className="flex-1 overflow-hidden">
              <div 
                className="flex items-center gap-8 animate-marquee"
                style={{ animation: 'marquee 30s linear infinite' }}
              >
                {[...tickerItems, ...tickerItems].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 whitespace-nowrap shrink-0">
                    <span className={`w-2 h-2 rounded-full ${item.type === 'deal' ? 'bg-emerald-500' : 'bg-orange-500'}`}></span>
                    <span className="text-white text-sm font-medium">{item.text}</span>
                    <span className="text-slate-500 text-xs">· {item.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Advertisement Section */}
      <section className="py-16 bg-slate-50" data-testid="featured-ads-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <p className="text-sm font-medium text-amber-600 uppercase tracking-wider">Featured Opportunities</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Premium Listings</h2>
            </div>
            <Link to="/marketplace">
              <Button variant="outline" className="hidden md:flex">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading featured listings...</p>
            </div>
          ) : featuredListings.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No featured listings available</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {featuredListings.map((listing, index) => (
                <Card key={listing.id} className="group overflow-hidden border-2 border-amber-100 hover:border-amber-300 hover:shadow-xl transition-all duration-300" data-testid={`featured-ad-${listing.id}`}>
                  <div className="relative">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-48 bg-slate-200 flex items-center justify-center">
                        <Building2 className="w-12 h-12 text-slate-400" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        {index === 0 ? "Featured" : index === 1 ? "Hot Deal" : "Top Pick"}
                      </span>
                    </div>
                    {listing.verified && (
                      <div className="absolute top-3 left-3">
                        <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                          Verified
                        </span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{listing.name}</h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">{listing.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <MapPin className="w-4 h-4" />
                        {listing.location}
                      </div>
                      <span className="text-xs text-slate-500">{listing.industry}</span>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                      <span className="text-2xl font-bold text-slate-900">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0
                        }).format(listing.asking_price)}
                      </span>
                      <Link to={`/listing/${listing.id}`}>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`view-details-${listing.id}`}>
                          View Details
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Franchise Opportunities Section */}
      <section className="py-20 bg-white" data-testid="franchise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-purple-100 text-purple-700 text-sm font-bold px-4 py-2 rounded-full mb-4">
              FRANCHISE OPPORTUNITIES
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Start Your Own Franchise</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join established brands with proven business models and ongoing support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {franchises.map((franchise) => (
              <Card key={franchise.id} className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-purple-200" data-testid={`franchise-${franchise.id}`}>
                <div className="relative overflow-hidden">
                  <img
                    src={franchise.image}
                    alt={franchise.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-bold text-lg">{franchise.brand}</h3>
                    <p className="text-white/80 text-sm">{franchise.name}</p>
                  </div>
                </div>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">Investment</span>
                    <span className="text-sm font-bold text-slate-900">{franchise.investment}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">Expected ROI</span>
                    <span className="text-sm font-bold text-emerald-600">{franchise.roi}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">Space Required</span>
                    <span className="text-sm font-bold text-slate-900">{franchise.area}</span>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 mb-1">Locations</p>
                    <p className="text-sm font-medium text-slate-900">{franchise.locations}</p>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 mb-1">Support Provided</p>
                    <p className="text-sm text-slate-700">{franchise.support}</p>
                  </div>
                  <Button 
                    onClick={() => handleFranchiseEnquiry(franchise)}
                    className="w-full bg-purple-600 hover:bg-purple-700 mt-4" 
                    size="sm" 
                    data-testid={`enquire-franchise-${franchise.id}`}
                  >
                    Enquire Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/franchises">
              <Button variant="outline" size="lg" className="border-purple-200 text-purple-700 hover:bg-purple-50">
                View All Franchise Opportunities
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* B2B Rentals Section */}
      <section className="py-20 bg-slate-50" data-testid="rentals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-emerald-100 text-emerald-700 text-sm font-bold px-4 py-2 rounded-full mb-4">
              B2B RENTALS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Rent Equipment & Spaces</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Find heavy equipment, commercial spaces, and transport services for your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Equipment Card */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-orange-200 overflow-hidden">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-8 text-center">
                <Wrench className="w-16 h-16 text-white mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white">Equipment</h3>
                <p className="text-orange-100 mt-2">Hywa, JCB, Cranes & more</p>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-2 text-slate-600 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Construction Equipment
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Industrial Machinery
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Generators & Tools
                  </li>
                </ul>
                <Link to="/rentals?category=equipment">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600">
                    Browse Equipment
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Spaces Card */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-blue-200 overflow-hidden">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-8 text-center">
                <Building2 className="w-16 h-16 text-white mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white">Spaces</h3>
                <p className="text-blue-100 mt-2">Commercial & Co-working</p>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-2 text-slate-600 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Commercial Shops
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Warehouses & Godowns
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Co-working Spaces
                  </li>
                </ul>
                <Link to="/rentals?category=spaces">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600">
                    Browse Spaces
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Transport Card */}
            <Card className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-green-200 overflow-hidden">
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-8 text-center">
                <Truck className="w-16 h-16 text-white mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white">Transport</h3>
                <p className="text-green-100 mt-2">Trucks, Fleet & Logistics</p>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-2 text-slate-600 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Trucks & Tempos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Containers & Tankers
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Fleet Services
                  </li>
                </ul>
                <Link to="/rentals?category=transport">
                  <Button className="w-full bg-green-500 hover:bg-green-600">
                    Browse Transport
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/rentals">
              <Button variant="outline" size="lg" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                View All Rentals
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            {user && (
              <Link to="/list-rental" className="ml-4">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                  List Your Rental
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Sponsored Banner Ad */}
      <section className="py-12" data-testid="sponsored-banner-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`bg-gradient-to-r ${sponsoredBanner.bgGradient} rounded-2xl p-8 md:p-12 text-white relative overflow-hidden`}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
            <div className="relative z-10 max-w-3xl">
              <div className="inline-block bg-white/20 text-xs font-bold px-3 py-1 rounded-full mb-4">
                SPONSORED
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{sponsoredBanner.title}</h2>
              <p className="text-lg text-slate-300 mb-6">{sponsoredBanner.description}</p>
              <Link to="/list-business">
                <Button className="bg-orange-500 text-white hover:bg-orange-600 h-12 px-8 font-semibold">
                  {sponsoredBanner.cta}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Why Choose ReBusi</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight">Built for Trust & Growth</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-orange-200 transition-all">
                  <div className="bg-orange-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-medium text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-base leading-relaxed text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer Testimonials Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-amber-100 text-amber-700 text-sm font-bold px-4 py-2 rounded-full mb-4">
              CUSTOMER STORIES
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">What Our Users Say</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real experiences from business owners, buyers, and consultants who found success with ReBusi
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 - Business Seller */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <Quote className="w-10 h-10 text-orange-100 mb-4" />
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "Sold my manufacturing unit within 3 months! The deal tracking system made the entire process transparent. 
                  ReBusi connected me with serious buyers and the escrow feature gave both parties peace of mind."
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                    RK
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Rajesh Kumar</p>
                    <p className="text-sm text-slate-500">Sold Manufacturing Business, Mumbai</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 - Business Buyer */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <Quote className="w-10 h-10 text-emerald-100 mb-4" />
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "Found my dream cafe business through ReBusi. The verified listings gave me confidence, and the 
                  AI chatbot helped me understand the financials better. Smooth acquisition process!"
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                    PS
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Priya Sharma</p>
                    <p className="text-sm text-slate-500">Acquired Cafe Business, Bangalore</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 - Consultant */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <Quote className="w-10 h-10 text-purple-100 mb-4" />
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "As a business consultant, ReBusi has become my primary platform. The booking system is seamless, 
                  and I've helped close over 15 deals. Great support team and professional network!"
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                    AM
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Amit Mehta</p>
                    <p className="text-sm text-slate-500">Business Consultant, Delhi NCR</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trust Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 bg-white rounded-2xl p-8 shadow-sm">
            <div className="text-center">
              <p className="text-4xl font-bold text-orange-500">500+</p>
              <p className="text-slate-600 mt-1">Businesses Listed</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-600">150+</p>
              <p className="text-slate-600 mt-1">Deals Closed</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-orange-500">50+</p>
              <p className="text-slate-600 mt-1">Expert Consultants</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-amber-600">4.8</p>
              <p className="text-slate-600 mt-1">Average Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-2xl p-12 md:p-16 text-center">
            <Building2 className="w-16 h-16 text-orange-500 mx-auto mb-6" />
            <h2 className="text-4xl font-semibold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses and consultants already using ReBusi
            </p>
            <Link to="/auth">
              <Button className="bg-orange-500 text-white hover:bg-orange-600 h-12 px-8" data-testid="get-started-button">
                Get Started Today
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Franchise Enquiry Dialog */}
      <Dialog open={enquiryDialog} onOpenChange={setEnquiryDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Franchise Enquiry</DialogTitle>
            <DialogDescription>
              {selectedFranchise && (
                <span className="text-base font-medium text-purple-600">
                  {selectedFranchise.brand} - {selectedFranchise.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEnquiry} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="enquiry-name">Full Name</Label>
              <Input
                id="enquiry-name"
                value={enquiryForm.name}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                required
                data-testid="enquiry-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-email">Email</Label>
              <Input
                id="enquiry-email"
                type="email"
                value={enquiryForm.email}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                required
                data-testid="enquiry-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-mobile">Mobile Number</Label>
              <Input
                id="enquiry-mobile"
                type="tel"
                value={enquiryForm.mobile}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, mobile: e.target.value })}
                required
                data-testid="enquiry-mobile-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enquiry-message">Message</Label>
              <Textarea
                id="enquiry-message"
                rows={4}
                value={enquiryForm.message}
                onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                required
                data-testid="enquiry-message-input"
              />
            </div>
            {selectedFranchise && (
              <div className="bg-purple-50 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Investment Required:</span>
                  <span className="font-bold text-slate-900">{selectedFranchise.investment}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Expected ROI:</span>
                  <span className="font-bold text-emerald-600">{selectedFranchise.roi}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Space Required:</span>
                  <span className="font-bold text-slate-900">{selectedFranchise.area}</span>
                </div>
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full bg-purple-600 hover:bg-purple-700" 
              data-testid="submit-enquiry-button"
              disabled={submittingEnquiry}
            >
              {submittingEnquiry ? "Submitting..." : "Submit Enquiry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
}